import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, gql } from '@apollo/client';

const UPDATE_PREFS_GQL = gql`
  mutation updateAdminPreferences($id: String!, $preferences: String!) {
    updateAdminPreferences(id: $id, preferences: $preferences) {
      err
      msg
    }
  }
`;

export const THEME_OPTIONS = [
  { value: 'ocean', label: '🌊 海洋藍 (預設)' },
  { value: 'forest', label: '🌲 森林綠' },
  { value: 'sunset', label: '🌅 日落橘' },
  { value: 'lavender', label: '💜 薰衣草紫' },
  { value: 'mint', label: '🍃 薄荷清涼' },
  { value: 'cherry', label: '🌸 櫻花粉' },
  { value: 'coffee', label: '☕ 濃縮咖啡' },
  { value: 'dark', label: '🌑 暗黑模式' }
];

export const THEME_COLORS = {
  ocean: { primary: '#1890ff', sidebarBg: '#f9f9f9', text: '#000000', subText: '#666666', containerBg: '#ffffff' },
  forest: { primary: '#389e0d', sidebarBg: '#f6ffed', text: '#000000', subText: '#666666', containerBg: '#ffffff' },
  sunset: { primary: '#d46b08', sidebarBg: '#fff7e6', text: '#000000', subText: '#666666', containerBg: '#ffffff' },
  lavender: { primary: '#722ed1', sidebarBg: '#f9f0ff', text: '#000000', subText: '#666666', containerBg: '#ffffff' },
  mint: { primary: '#13c2c2', sidebarBg: '#e6fffb', text: '#000000', subText: '#666666', containerBg: '#ffffff' },
  cherry: { primary: '#eb2f96', sidebarBg: '#fff0f6', text: '#000000', subText: '#666666', containerBg: '#ffffff' },
  coffee: { primary: '#874d00', sidebarBg: '#f5efe6', text: '#000000', subText: '#666666', containerBg: '#ffffff' },
  dark: { primary: '#177ddc', sidebarBg: '#141414', text: '#d9d9d9', subText: '#888888', containerBg: '#1f1f1f' }
};

const ThemeContext = createContext({
  globalTheme: 'ocean',
  language: 'zh-tw',
  setGlobalTheme: () => {},
  setGlobalLanguage: () => {}
});

export const useGlobalTheme = () => useContext(ThemeContext);

export const GlobalThemeProvider = ({ children }) => {
  const [globalTheme, _setGlobalTheme] = useState('ocean');
  const [language, _setLanguage] = useState('zh-tw');
  const [loginLanguage, _setLoginLanguage] = useState(() => {
    return localStorage.getItem('login-lang') || 'zh-tw';
  });
  const location = useLocation();
  const { i18n } = useTranslation();
  const [updatePrefs] = useMutation(UPDATE_PREFS_GQL);

  const isLoginPage = location.pathname.includes('/login');

  // Load from local storage on mount
  useEffect(() => {
    const rawAdmin = localStorage.getItem('admin-info');
    if (rawAdmin) {
      try {
        const admin = JSON.parse(rawAdmin);
        if (admin.preferences) {
          try {
            const pref = JSON.parse(admin.preferences);
            if (pref.theme) _setGlobalTheme(pref.theme);
            if (pref.language) {
              _setLanguage(pref.language);
              if (!isLoginPage) {
                i18n.changeLanguage(pref.language);
              }
            }
          } catch (prefError) {
            console.error('Error parsing admin preferences string:', prefError);
            // If preferences is invalid string, keep defaults
          }
        }
      } catch (adminError) {
        console.error('Error parsing admin-info from localStorage:', adminError);
      }
    }
    
    // Always apply login language if on login page
    if (isLoginPage) {
      i18n.changeLanguage(loginLanguage);
    }
  }, [i18n, isLoginPage, loginLanguage]);

  const saveToBackend = (newTheme, newLang) => {
    const rawAdmin = localStorage.getItem('admin-info');
    if (!rawAdmin) return;
    try {
      const admin = JSON.parse(rawAdmin);
      const newPrefs = { theme: newTheme, language: newLang };
      const strPrefs = JSON.stringify(newPrefs);
      admin.preferences = strPrefs;
      localStorage.setItem('admin-info', JSON.stringify(admin));

      if (admin.adminId) {
        updatePrefs({ variables: { id: admin.adminId, preferences: strPrefs } }).catch(e => console.log('Error updating preferences on backend:', e));
      }
    } catch (e) {
      console.error('Error saving preferences to backend/localStorage:', e);
    }
  };

  const handleSetTheme = (theme) => {
    _setGlobalTheme(theme);
    saveToBackend(theme, language);
  };

  const handleSetLanguage = (lang) => {
    if (isLoginPage) {
      _setLoginLanguage(lang);
      localStorage.setItem('login-lang', lang);
      i18n.changeLanguage(lang);
    } else {
      _setLanguage(lang);
      i18n.changeLanguage(lang);
      saveToBackend(globalTheme, lang);
    }
  };

  const activeTheme = isLoginPage ? 'ocean' : globalTheme;
  const activeLanguage = isLoginPage ? loginLanguage : language;

  // Sync i18n language with the active language
  useEffect(() => {
    if (i18n.language !== activeLanguage) {
      i18n.changeLanguage(activeLanguage);
    }
  }, [activeLanguage, i18n]);

  return (
    <ThemeContext.Provider value={{ 
      globalTheme: activeTheme, 
      language: activeLanguage, 
      setGlobalTheme: handleSetTheme,
      setGlobalLanguage: handleSetLanguage 
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
